import type { KeyboardEvent, ReactNode } from 'react';

import { cx } from './classNames';
import { Icon } from './Icon';

/** Reiterleiste (REQ-25 §2) — Vorlage ist `Tabs.tsx` der ChinaApp. */
export interface TabItem {
  id: string;
  label: string;
  /** Kleine Zahl hinter der Beschriftung, z. B. Anzahl der Positionen. */
  count?: number;
  /**
   * Fehlermarke hinter der Beschriftung, z. B. weil auf dem Reiter ein Pflichtfeld leer ist
   * (REQ-27 §2.2). Fuer die Vorlesehilfe steht neben dem Symbol das Wort "fehlerhaft".
   */
  invalid?: boolean;
  disabled?: boolean;
}

export interface TabsProps {
  /** Grundlage der Ids von Reitern und Panels. */
  id: string;
  items: TabItem[];
  value: string;
  onChange: (id: string) => void;
  ariaLabel: string;
  /** Reiter teilen sich die volle Breite. */
  stretch?: boolean;
}

export interface TabPanelProps {
  tabsId: string;
  id: string;
  active: boolean;
  children: ReactNode;
}

const tabId = (tabsId: string, id: string) => `${tabsId}-tab-${id}`;
const panelId = (tabsId: string, id: string) => `${tabsId}-panel-${id}`;

/**
 * Pfeiltasten wechseln den Reiter und aktivieren ihn sofort; nur der aktive Reiter liegt in
 * der Tab-Reihenfolge. Das ist das Muster der WAI-ARIA Authoring Practices für automatisch
 * aktivierende Reiter.
 */
export function Tabs(props: TabsProps) {
  const { id, items, onChange, value } = props;

  const enabled = items.filter((item) => item.disabled !== true);
  // Steht `value` auf keinem bedienbaren Reiter, bleibt die Leiste trotzdem per Tab erreichbar.
  const tabbableId = enabled.some((item) => item.id === value) ? value : enabled[0]?.id;

  /** Nächster bedienbarer Reiter in Richtung `delta`, am Ende umlaufend. */
  const step = (from: number, delta: 1 | -1): TabItem | undefined => {
    for (let offset = 1; offset <= items.length; offset += 1) {
      const index = (((from + delta * offset) % items.length) + items.length) % items.length;
      const item = items[index];
      if (item !== undefined && item.disabled !== true) return item;
    }
    return undefined;
  };

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const current = items.findIndex((item) => item.id === value);
    let next: TabItem | undefined;
    if (event.key === 'ArrowRight') next = step(current, 1);
    else if (event.key === 'ArrowLeft') next = step(current, -1);
    else if (event.key === 'Home') next = enabled[0];
    else if (event.key === 'End') next = enabled[enabled.length - 1];
    else return;

    event.preventDefault();
    if (next === undefined) return;
    onChange(next.id);
    document.getElementById(tabId(id, next.id))?.focus();
  };

  return (
    <div
      role="tablist"
      aria-label={props.ariaLabel}
      className={cx('iwm-tabs', props.stretch === true && 'iwm-tabs--stretch')}
      onKeyDown={onKeyDown}
    >
      {items.map((item) => {
        const active = item.id === value;
        return (
          <button
            key={item.id}
            type="button"
            role="tab"
            id={tabId(id, item.id)}
            aria-selected={active}
            aria-controls={panelId(id, item.id)}
            tabIndex={item.id === tabbableId ? 0 : -1}
            disabled={item.disabled === true}
            className={cx(
              'iwm-tab',
              active && 'iwm-tab--active',
              item.invalid === true && 'iwm-tab--invalid',
            )}
            onClick={() => {
              onChange(item.id);
            }}
          >
            <span className="iwm-tab-label">{item.label}</span>
            {item.invalid === true ? (
              <>
                <Icon iconClass="fa-solid fa-circle-exclamation" className="iwm-tab-invalid" />
                <span className="iwm-tab-invalid-text">fehlerhaft</span>
              </>
            ) : null}
            {item.count === undefined ? null : <span className="iwm-tab-count">{item.count}</span>}
          </button>
        );
      })}
    </div>
  );
}

/**
 * Inhalt eines Reiters. Inaktive Panels bleiben im DOM (`hidden`) und behalten damit ihren
 * Zustand; wer erst beim Aufrufen laden will, rendert den Inhalt abhängig von `active`.
 */
export function TabPanel(props: TabPanelProps) {
  return (
    <div
      role="tabpanel"
      id={panelId(props.tabsId, props.id)}
      aria-labelledby={tabId(props.tabsId, props.id)}
      hidden={!props.active}
      tabIndex={0}
      className="iwm-tabpanel"
    >
      {props.children}
    </div>
  );
}

export default Tabs;
